import React, { useState, useEffect } from "react";
import { axiosInstance } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const WebsitesTab = () => {
  const [websiteList, setWebsiteList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [websiteForm, setWebsiteForm] = useState({ name: "" });
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [showWebsiteDialog, setShowWebsiteDialog] = useState(false);

  useEffect(() => {
    fetchWebsiteList();
  }, []);

  const fetchWebsiteList = async () => {
    try {
      const response = await axiosInstance.get("/website-list");
      setWebsiteList(response.data);
    } catch (error) {
      toast.error("Gagal memuat data website");
    }
  };

  const handleWebsiteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingWebsite) {
        await axiosInstance.put(`/website-list/${editingWebsite.id}`, websiteForm);
        toast.success("Website berhasil diupdate");
      } else {
        await axiosInstance.post("/website-list", websiteForm);
        toast.success("Website berhasil ditambahkan");
      }
      setShowWebsiteDialog(false);
      setWebsiteForm({ name: "" });
      setEditingWebsite(null);
      fetchWebsiteList();
    } catch (error) {
      toast.error("Gagal menyimpan website");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWebsite = async (id) => {
    if (!window.confirm("Yakin ingin menghapus website ini?")) return;
    try {
      await axiosInstance.delete(`/website-list/${id}`);
      toast.success("Website berhasil dihapus");
      fetchWebsiteList();
    } catch (error) {
      toast.error("Gagal menghapus website");
    }
  };

  const openEditWebsite = (item) => {
    setEditingWebsite(item);
    setWebsiteForm({ name: item.name });
    setShowWebsiteDialog(true);
  };

  return (
    <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30" data-testid="websites-tab-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-red-50">Daftar Website</CardTitle>
            <CardDescription className="text-gray-400">Kelola nama-nama website</CardDescription>
          </div>
          <Dialog open={showWebsiteDialog} onOpenChange={setShowWebsiteDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingWebsite(null);
                  setWebsiteForm({ name: "" });
                }}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                data-testid="add-website-button"
              >
                <Plus className="mr-2" size={18} />
                Tambah Website
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1b] border-red-900/30">
              <DialogHeader>
                <DialogTitle className="text-red-50">
                  {editingWebsite ? "Edit Website" : "Tambah Website Baru"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleWebsiteSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="website-name" className="text-gray-300">Nama Website</Label>
                  <Input
                    id="website-name"
                    value={websiteForm.name}
                    onChange={(e) => setWebsiteForm({ name: e.target.value })}
                    required
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="website-name-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                  disabled={loading}
                  data-testid="save-website-button"
                >
                  {loading ? "Menyimpan..." : editingWebsite ? "Update" : "Tambah"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {websiteList.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg border border-red-900/20"
              data-testid={`website-item-${item.id}`}
            >
              <div className="font-medium text-white">{item.name}</div>
              <div className="flex gap-2">
                <Button
                  onClick={() => openEditWebsite(item)}
                  variant="ghost"
                  size="sm"
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                  data-testid={`edit-website-${item.id}`}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  onClick={() => handleDeleteWebsite(item.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                  data-testid={`delete-website-${item.id}`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
          {websiteList.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-400">Belum ada data website</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WebsitesTab;
